import React from "react";
import type { TableRowEditorField } from "./table-row-editor-fields";

export type TableRowModalPreviewMode = "create" | "edit";

export interface TableRowModalPreviewRequest {
  id: string;
  componentId: string;
  componentName: string;
  mode: TableRowModalPreviewMode;
  fields: TableRowEditorField[];
  rowData: Record<string, unknown>;
  mobileViewport?: boolean;
  readOnly?: boolean;
  onClose: () => void;
  onSave: (rowData: Record<string, unknown>) => void | Promise<unknown>;
  onDelete?: () => void | Promise<unknown>;
}

interface TableModalPreviewContextValue {
  enabled: boolean;
  activeRequest: TableRowModalPreviewRequest | null;
  openPreview: (request: TableRowModalPreviewRequest) => void;
  closePreview: () => void;
}

const defaultContext: TableModalPreviewContextValue = {
  enabled: false,
  activeRequest: null,
  openPreview: () => undefined,
  closePreview: () => undefined,
};

const TableModalPreviewContext =
  React.createContext<TableModalPreviewContextValue>(defaultContext);

export const TableModalPreviewProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [activeRequest, setActiveRequest] =
    React.useState<TableRowModalPreviewRequest | null>(null);

  const openPreview = React.useCallback(
    (request: TableRowModalPreviewRequest) => {
      setActiveRequest(request);
    },
    [],
  );

  const closePreview = React.useCallback(() => {
    setActiveRequest(null);
  }, []);

  const value = React.useMemo(
    () => ({
      enabled: true,
      activeRequest,
      openPreview,
      closePreview,
    }),
    [activeRequest, closePreview, openPreview],
  );

  return (
    <TableModalPreviewContext.Provider value={value}>
      {children}
    </TableModalPreviewContext.Provider>
  );
};

export const useTableModalPreview = (): TableModalPreviewContextValue =>
  React.useContext(TableModalPreviewContext);
